import { Key, useEffect, useMemo } from 'react';

import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import { AddMerchantApplicationPayload } from 'types/graphql';

import { useMutation } from '@redwoodjs/web';

import { FileUploadDropDown } from 'src/components/FormikComponents';
import MainCard from 'src/components/MainCard/MainCard';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import {
    DOWNLOAD_B1_FORM,
    DOWNLOAD_PARTNERSHIP_DEED_FILE,
} from 'src/graphql/mutations/downloadB1Form.mutation';
import { UPDATE_MERCHANT_APPLICATION_DOCUMENTS_MUTATION } from 'src/graphql/mutations/updateApplicationDoucments.mutation';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { hasRequiredSupportingDocuments } from 'src/utils/utils';

import BeneficialOwnerSupportingDocuments from './components/BeneficialOwnerSupportingDocuments';
import ExtraSupportingDocuments from './components/ExtraSupportingDocuments';

const idDocumentTypes = [
    {
        key: 'DRIVER_LICENSE',
        value: 'Valid Driver License',
    },
    {
        key: 'PASSPORT',
        value: 'Valid Passport',
    },
];
const addressDocumentTypes = [
    {
        key: 'COUNCIL_TAX_BILL',
        value: 'Council Tax Bill',
    },
    {
        key: 'ORIGINAL_MORTGAGE_STATEMENT',
        value: 'Original Mortgage Statement',
    },
    {
        key: 'UTILITY_BILL',
        value: 'Utility Bill',
    },
    {
        key: 'CARD_STATEMENT',
        value: 'Bank/Buiding Society/Credit Card Statement',
    },
    {
        key: 'INSURANCE_CERTIFICATE',
        value: 'House/Motor Insurance Certificate/Policy',
    },
];

const bankDocumentTypes = [
    {
        key: 'BANK_STATEMENT',
        value: 'Bank Statement',
    },
];

export type SupportingDocumentsFormValues = {
    idTye: string;
    idFile: string;
    addressType: string;
    addressFile: string;
};

export interface SupportingDocumentsProps {
    setDocumentsSubmitState: (value: boolean) => void;
    isUploadingDocuments?: boolean;
    application?: AddMerchantApplicationPayload;
    onBack?: () => void;
}
export const SupportingDocuments = ({
    setDocumentsSubmitState,
    isUploadingDocuments,
    application,
    onBack,
}: SupportingDocumentsProps) => {
    // get intial values for all principals
    const { addMerchant, merchantApplication, handleBack } = useAddMerchantContext();
    const [getB1Form, { data: b1FormData }] = useMutation(DOWNLOAD_B1_FORM, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
    });

    const [downloadPartnershipDeed, { data: partnershipDeedData }] = useMutation(
        DOWNLOAD_PARTNERSHIP_DEED_FILE,
        {
            onError: (error) => {
                return showErrorToast(error.message);
            },
        }
    );

    const [updateDocumentsMutation] = useMutation(UPDATE_MERCHANT_APPLICATION_DOCUMENTS_MUTATION, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: ({ updateMerchantSupportingDocuments }) => {
            const { supportingDocuments } = updateMerchantSupportingDocuments;
            if (supportingDocuments) {
                setDocumentsSubmitState(true);
                showSuccessToast('Successfully saved documents!');
                onBack();
            }
        },
    });

    const settlementAccountSameAsChargeable = (merchantApplication ?? application)
        ?.businessInformation?.settlementAccountSameAsChargeable;

    const businessType = (merchantApplication ?? application)?.merchantInformation?.aboutBusiness
        ?.businessType;

    const merchantPrincipals =
        (merchantApplication ?? application)?.merchantInformation?.principals ?? [];

    const supportingDocumentPrincipals =
        merchantApplication ?? application?.supportingDocuments?.principalDocuments ?? [];

    const initialPrincipals =
        supportingDocumentPrincipals.length > 0
            ? supportingDocumentPrincipals
            : merchantPrincipals.filter((el) => {
                  return el?.isBeneficialOwner;
              });

    const existingOutletDocuments =
        merchantApplication ?? application?.supportingDocuments?.outletDocuments ?? [];
    const merchantOutlets =
        (merchantApplication ?? application)?.merchantInformation?.outlets ?? [];

    const initialOutlets =
        existingOutletDocuments?.length > 0 ? existingOutletDocuments : merchantOutlets;

    useEffect(() => {
        let b1FormId = merchantApplication?.merchantInformation?.b1FormId;
        if (application) {
            b1FormId = application?.merchantInformation?.b1FormId;
        }
        if (b1FormId) {
            getB1Form({ variables: { id: Number.parseInt(b1FormId, 10) } });
        }

        let partnershipDeedId = merchantApplication?.merchantInformation?.partnershipDeedId;
        if (application) {
            partnershipDeedId = application?.merchantInformation?.partnershipDeedId;
        }

        if (partnershipDeedId) {
            downloadPartnershipDeed({ variables: { id: Number.parseInt(partnershipDeedId, 10) } });
        }
    }, [merchantApplication, application, getB1Form, downloadPartnershipDeed]);

    const initialValues = useMemo(() => {
        return {
            b1FormFile: {
                locationUrl: b1FormData?.downloadB1Form?.url,
            },
            partnershipDeedFile: {
                locationUrl: partnershipDeedData?.downloadPartnershipDeedFile?.url,
            },
            principalDocuments: initialPrincipals,
            outletDocuments: initialOutlets,
            ...(merchantApplication ?? application)?.supportingDocuments,
        };
    }, [
        b1FormData,
        partnershipDeedData,
        application,
        initialOutlets,
        initialPrincipals,
        merchantApplication,
    ]);

    const onSubmit = async (input: object) => {
        if (isUploadingDocuments) {
            if (
                hasRequiredSupportingDocuments(
                    application?.merchantInformation?.aboutBusiness?.businessType,
                    application?.businessInformation?.settlementAccountSameAsChargeable,
                    input
                )
            ) {
                const { id: applicationId } = application;
                await updateDocumentsMutation({
                    variables: {
                        input: {
                            applicationId,
                            supportingDocuments: {
                                ...input,
                                isStepCompleted: true,
                            },
                        },
                    },
                });
            } else {
                showErrorToast('Please fill in required fields');
            }
        } else {
            addMerchant({
                supportingDocuments: {
                    ...input,
                    isStepCompleted: true,
                },
            });
        }
    };

    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
            {({ values }) => {
                const acceptedFilesToUpload = {
                    'text/pdf': ['.pdf', '.PDF', '.Pdf', '.pDf', '.pdF'],
                    'image/jpeg': [
                        '.jpeg',
                        '.jpg',
                        '.png',
                        '.JPEG',
                        '.JPG',
                        '.PNG',
                        '.Jpeg',
                        '.Jpg',
                        '.png',
                        '.jPeg',
                        '.jPg',
                        '.Png',
                        '.jpEg',
                        '.jpg',
                        '.pNg',
                        '.jpeG',
                        '.jpG',
                        '.pnG',
                    ],
                };

                return (
                    <Form
                        style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
                        autoComplete="off"
                        role={'form'}
                    >
                        <FieldArray
                            name="principalDocuments"
                            render={() => {
                                const beneficialOwners = merchantApplication?.merchantInformation
                                    ?.principals?.length
                                    ? merchantApplication?.merchantInformation?.principals.filter(
                                          (el: { isBeneficialOwner: boolean }) => {
                                              return el.isBeneficialOwner;
                                          }
                                      )
                                    : (application?.merchantInformation?.principals?.filter(
                                          (el: { isBeneficialOwner: boolean }) => {
                                              return el.isBeneficialOwner;
                                          }
                                      ) ?? []);

                                if (beneficialOwners?.length === 0) {
                                    return (
                                        <MainCard>
                                            <Typography
                                                fontSize="24px"
                                                color="#1D1D1B"
                                                marginBottom="8px"
                                            >
                                                No Beneficial Owners
                                            </Typography>
                                        </MainCard>
                                    );
                                }

                                return beneficialOwners.map((principal: string, index: Key) => {
                                    return (
                                        <BeneficialOwnerSupportingDocuments
                                            key={index}
                                            principal={principal}
                                            index={index}
                                            isUploadingDocuments={isUploadingDocuments}
                                            idDocumentTypes={idDocumentTypes}
                                            addressDocumentTypes={addressDocumentTypes}
                                            acceptedFilesToUpload={acceptedFilesToUpload}
                                            FileUploadDropDown={FileUploadDropDown}
                                        />
                                    );
                                });
                            }}
                        />

                        <MainCard>
                            <Typography fontSize="24px" color="#1D1D1B" marginBottom="8px">
                                Proof of Bank
                            </Typography>
                            <Divider
                                sx={{
                                    borderColor: 'warning.main',
                                    marginBottom: '34px',
                                }}
                            />
                            <Grid container rowSpacing={2} columnSpacing={2} rowGap={2}>
                                {settlementAccountSameAsChargeable === true ? (
                                    <Grid item xs={12} md={6}>
                                        <FileUploadDropDown
                                            name="proofOfBank.chargeableAccountFile"
                                            fileTypes={bankDocumentTypes.map(({ key, value }) => {
                                                return {
                                                    value,
                                                    label: key,
                                                };
                                            })}
                                            acceptedFileTypes={acceptedFilesToUpload}
                                            label="Settlement Account & Chargeable Account"
                                            placeholder="Proof of Bank"
                                            isReadOnly={isEditingDisabled}
                                        />
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <FileUploadDropDown
                                                name="proofOfBank.settlementAccountFile"
                                                fileTypes={bankDocumentTypes.map(
                                                    ({ key, value }) => {
                                                        return {
                                                            value,
                                                            label: key,
                                                        };
                                                    }
                                                )}
                                                acceptedFileTypes={acceptedFilesToUpload}
                                                label="Settlement Account"
                                                placeholder="Proof of Bank"
                                                isReadOnly={isEditingDisabled}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FileUploadDropDown
                                                name="proofOfBank.chargeableAccountFile"
                                                fileTypes={bankDocumentTypes.map(
                                                    ({ key, value }) => {
                                                        return {
                                                            value,
                                                            label: key,
                                                        };
                                                    }
                                                )}
                                                acceptedFileTypes={acceptedFilesToUpload}
                                                label="Chargeable Account"
                                                placeholder="Proof of Bank"
                                                isReadOnly={isEditingDisabled}
                                            />
                                        </Grid>
                                    </>
                                )}
                                {values.outletDocuments
                                    ?.filter((el) => {
                                        return (
                                            el.bankingDetails.serviceChargeCalculatedAt ===
                                            'OUTLETLEVEL'
                                        );
                                    })
                                    .map((outlet, index) => {
                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                                key={`proofOfBank.outletFiles[${index}]`}
                                            >
                                                <FileUploadDropDown
                                                    name={`proofOfBank.outletFiles[${index}]`}
                                                    fileTypes={bankDocumentTypes.map(
                                                        ({ key, value }) => {
                                                            return {
                                                                value,
                                                                label: key,
                                                            };
                                                        }
                                                    )}
                                                    acceptedFileTypes={acceptedFilesToUpload}
                                                    label={`Outlet name: ${outlet.name}`}
                                                    placeholder="Proof of Bank"
                                                    isReadOnly={isEditingDisabled}
                                                />
                                            </Grid>
                                        );
                                    })}
                            </Grid>
                        </MainCard>
                        <ExtraSupportingDocuments
                            businessType={businessType}
                            acceptedFilesToUpload={acceptedFilesToUpload}
                            isEditingDisabled={isEditingDisabled}
                        />
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                sx={{
                                    alignSelf: 'end',
                                    marginTop: '24px',
                                    marginRight: '22px',
                                }}
                                onClick={() => {
                                    if (onBack) {
                                        onBack();
                                    } else {
                                        handleBack();
                                    }
                                }}
                                variant="back-button"
                                role="button"
                            >
                                {isUploadingDocuments ? 'Cancel' : 'Back'}
                            </Button>

                            <Button
                                sx={{
                                    alignSelf: 'end',
                                    marginTop: '24px',
                                }}
                                type="submit"
                                variant="main"
                                role="button"
                            >
                                {isUploadingDocuments ? 'Save' : 'Next'}
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};
